/**
 * LeadChatWidget — floating bubble for unauthenticated landing
 * visitors. Bot-first conversation, "Hablar con humano" hand-off.
 *
 * Storage
 *   `lumfi.lead.visitor_id` — UUID persisted in localStorage so the
 *   thread survives a page refresh. We generate it server-side on
 *   the first /init call to avoid leaking client RNG choices.
 *
 * Endpoints
 *   POST /api/public/lead-chat/init           → bootstrap visitor + welcome
 *   POST /api/public/lead-chat/send           → visitor → bot (returns AI reply)
 *   POST /api/public/lead-chat/request-human  → escalate to admin
 *   GET  /api/public/lead-chat/messages       → polling
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Send, X, UserRound, Sparkles, ArrowRight } from "lucide-react";
import axios from "axios";
import { tx } from "../../i18n";

const API = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = "lumfi.lead.visitor_id";

const COPY = {
  es: {
    title: "Chat con Lumfi",
    subtitle: "Respuestas al toque de LumiBot",
    open_aria: "Abrir chat",
    close_aria: "Cerrar chat",
    nameLabel: "¿Cómo te llamas?",
    namePlaceholder: "Ej: Carolina",
    emailLabel: "Email (opcional)",
    emailPlaceholder: "tu@email.com",
    startBtn: "Iniciar chat",
    inputPlaceholder: "Escribe tu mensaje…",
    sendAria: "Enviar mensaje",
    askHuman: "Hablar con humano",
    askingHuman: "Pidiendo humano…",
    humanRequested: "Listo, ya pedimos un humano. Te respondemos en breve.",
    typing: "LumiBot está escribiendo…",
    poweredBy: "Powered by LumiBot · IA",
    welcomeIntro:
      "Te respondo en segundos: planes, precios, cómo funciona y cualquier duda que tengas.",
    startSubtitle: "Charla con nuestro bot. Si necesitas, pasamos a un humano del equipo.",
  },
  en: {
    title: "Chat with Lumfi",
    subtitle: "Instant answers from LumiBot",
    open_aria: "Open chat",
    close_aria: "Close chat",
    nameLabel: "What's your name?",
    namePlaceholder: "E.g.: Carolina",
    emailLabel: "Email (optional)",
    emailPlaceholder: "you@email.com",
    startBtn: "Start chat",
    inputPlaceholder: "Type your message…",
    sendAria: "Send message",
    askHuman: "Talk to a human",
    askingHuman: "Requesting human…",
    humanRequested: "Done — we asked a human to join. We'll reply shortly.",
    typing: "LumiBot is typing…",
    poweredBy: "Powered by LumiBot · AI",
    welcomeIntro:
      "I'll reply in seconds: plans, pricing, how Lumfi works and anything else.",
    startSubtitle: "Chat with our bot. If you need, we'll route you to a human.",
  },
  pt: {
    title: "Chat com Lumfi",
    subtitle: "Respostas instantâneas do LumiBot",
    open_aria: "Abrir chat",
    close_aria: "Fechar chat",
    nameLabel: "Como você se chama?",
    namePlaceholder: "Ex: Carolina",
    emailLabel: "Email (opcional)",
    emailPlaceholder: "voce@email.com",
    startBtn: "Iniciar chat",
    inputPlaceholder: "Digite sua mensagem…",
    sendAria: "Enviar mensagem",
    askHuman: "Falar com humano",
    askingHuman: "Pedindo humano…",
    humanRequested: "Pronto — pedimos uma pessoa do time. Resposta em breve.",
    typing: "LumiBot está digitando…",
    poweredBy: "Powered by LumiBot · IA",
    welcomeIntro:
      "Respondo em segundos: planos, preços e qualquer dúvida sobre o app.",
    startSubtitle: "Converse com o bot. Se precisar, levamos a um humano do time.",
  },
};

function readVisitorId() {
  try { return localStorage.getItem(VISITOR_KEY) || null; } catch { return null; }
}
function writeVisitorId(id) {
  try { if (id) localStorage.setItem(VISITOR_KEY, id); } catch {}
}

export function LeadChatWidget({ lang = "es", country = null }) {
  const t = COPY[lang] || COPY.es;
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [visitorId, setVisitorId] = useState(() => readVisitorId());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [requestingHuman, setRequestingHuman] = useState(false);
  const [humanRequested, setHumanRequested] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(true);
  const pollRef = useRef(null);
  const scrollRef = useRef(null);

  const initThread = async ({ name: n, email: e } = {}) => {
    try {
      const res = await axios.post(`${API}/api/public/lead-chat/init`, {
        visitor_id: visitorId || undefined,
        name: n || name || undefined,
        email: e || email || undefined,
        country: country || undefined,
        lang,
      });
      const data = res.data || {};
      if (data.visitor_id) {
        writeVisitorId(data.visitor_id);
        setVisitorId(data.visitor_id);
      }
      if (Array.isArray(data.messages)) setMessages(data.messages);
      if (data?.lead?.human_requested) setHumanRequested(true);
      setStarted(true);
    } catch (err) {
      // No-op; widget shows a generic retry hint.
      console.warn("lead chat init failed", err);
    }
  };

  // Polling for fresh admin / bot replies while panel is open.
  useEffect(() => {
    if (!open || !visitorId) return;
    const poll = async () => {
      try {
        const r = await axios.get(
          `${API}/api/public/lead-chat/messages?visitor_id=${encodeURIComponent(visitorId)}`,
        );
        const incoming = r.data?.messages || [];
        setMessages((prev) => {
          if (incoming.length !== prev.length) {
            // Count newly arrived "out" messages we haven't seen yet
            const lastLocal = prev[prev.length - 1]?.id;
            const idx = incoming.findIndex((m) => m.id === lastLocal);
            const fresh = idx >= 0 ? incoming.slice(idx + 1) : incoming;
            const outDelta = fresh.filter((m) => m.direction === "out").length;
            if (outDelta > 0 && !open) setUnread((u) => u + outDelta);
            return incoming;
          }
          return prev;
        });
        if (r.data?.lead?.human_requested) setHumanRequested(true);
      } catch {/* swallow */}
    };
    pollRef.current = setInterval(poll, 7000);
    return () => clearInterval(pollRef.current);
  }, [open, visitorId]);

  // Auto-scroll to latest on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, started]);

  // Reset unread when opening.
  useEffect(() => { if (open) setUnread(0); }, [open]);

  // Stop the pulse after the user has seen the bubble.
  useEffect(() => {
    const id = setTimeout(() => setPulse(false), 6000);
    return () => clearTimeout(id);
  }, []);

  // If user already has a visitor_id (returning visitor), preload silently
  // so opening the chat instantly shows the history.
  useEffect(() => {
    if (visitorId && !started) {
      initThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = async (e) => {
    e?.preventDefault?.();
    await initThread({ name, email });
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || !visitorId || sending) return;
    setSending(true);
    // Optimistic append
    const local = {
      id: `local-${Date.now()}`,
      body, direction: "in", channel: "lead",
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, local]);
    setDraft("");
    try {
      const r = await axios.post(`${API}/api/public/lead-chat/send`, {
        visitor_id: visitorId, body, lang,
      });
      // Replace local + append AI reply if present.
      const sentMsg = r.data?.message;
      const aiText = r.data?.ai_reply;
      setMessages((prev) => {
        const without = prev.filter((m) => m.id !== local.id);
        const next = sentMsg ? [...without, sentMsg] : [...without, local];
        if (aiText) {
          next.push({
            id: `ai-${Date.now()}`,
            body: aiText,
            direction: "out",
            channel: "lead",
            ai_generated: true,
            created_at: new Date().toISOString(),
          });
        }
        return next;
      });
    } catch {
      // Roll back optimistic
      setMessages((prev) => prev.filter((m) => m.id !== local.id));
    } finally {
      setSending(false);
    }
  };

  const handleRequestHuman = async () => {
    if (!visitorId || requestingHuman || humanRequested) return;
    setRequestingHuman(true);
    try {
      await axios.post(`${API}/api/public/lead-chat/request-human`, {
        visitor_id: visitorId,
      });
      setHumanRequested(true);
      setMessages((m) => [
        ...m,
        {
          id: `system-${Date.now()}`,
          body: t.humanRequested,
          direction: "out",
          channel: "lead",
          system_event: "human_requested",
          created_at: new Date().toISOString(),
        },
      ]);
    } catch {/* ignored */} finally {
      setRequestingHuman(false);
    }
  };

  const hasMessages = useMemo(() => messages.length > 0, [messages]);

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3 pointer-events-none"
      data-testid="lead-chat-widget"
    >
      {/* Closed bubble */}
      {!open && (
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={() => setOpen(true)}
          data-testid="lead-chat-bubble"
          aria-label={t.open_aria}
          className={`relative mb-16 h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 pointer-events-auto ${pulse ? "lead-chat-pulse" : ""}`}
        >
          <MessageCircle className="h-6 w-6" />
          {unread > 0 && (
            <span
              data-testid="lead-chat-bubble-unread"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Open panel */}
      {open && (
        <div
          data-testid="lead-chat-panel"
          className="w-[calc(100vw-24px)] sm:w-[380px] h-[min(560px,calc(100vh-32px))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden pointer-events-auto"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-br from-emerald-700 to-emerald-600 text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <h3 className="font-semibold text-sm">{t.title}</h3>
                <p className="text-[11px] text-emerald-50/90">{t.subtitle}</p>
              </div>
            </div>
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={() => setOpen(false)}
              data-testid="lead-chat-close"
              aria-label={t.close_aria}
              className="text-white/85 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Pre-start form */}
          {!started && (
            <form
              onSubmit={handleStart}
              className="flex-1 overflow-y-auto p-5 space-y-4"
              data-testid="lead-chat-start-form"
            >
              <p className="text-sm text-slate-600 leading-relaxed">
                {t.startSubtitle}
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {t.nameLabel}
                </label>
                <input /* design-system: ALLOW legacy-todo */
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePlaceholder}
                  data-testid="lead-chat-name-input"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {t.emailLabel}
                </label>
                <input /* design-system: ALLOW legacy-todo */
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  data-testid="lead-chat-email-input"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                />
              </div>
              <button /* design-system: ALLOW legacy-todo */
                type="submit"
                disabled={!name.trim()}
                data-testid="lead-chat-start-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
              >
                {t.startBtn}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-[11px] text-slate-500 text-center pt-1">
                {t.welcomeIntro}
              </p>
            </form>
          )}

          {/* Chat thread */}
          {started && (
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/30"
                data-testid="lead-chat-messages"
              >
                {messages.map((m) => {
                  const mine = m.direction === "in";
                  const isSystem = m.system_event;
                  return (
                    <div
                      key={m.id}
                      className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      data-testid={`lead-chat-msg-${m.id}`}
                    >
                      <div
                        className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm shadow-sm ${
                          mine
                            ? "bg-emerald-600 text-white rounded-br-sm"
                            : isSystem
                              ? "bg-amber-50 text-amber-900 border border-amber-200 rounded-bl-sm"
                              : (m.ai_generated
                                  ? "bg-sky-50 text-sky-900 border border-sky-200 rounded-bl-sm"
                                  : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm")
                        }`}
                      >
                        {!mine && !isSystem && m.ai_generated && (
                          <div className="flex items-center gap-1 mb-1 text-[9px] font-bold uppercase tracking-wide text-sky-600">
                            <Sparkles className="h-2.5 w-2.5" /> LumiBot · IA
                          </div>
                        )}
                        {!mine && !isSystem && !m.ai_generated && (
                          <div className="flex items-center gap-1 mb-1 text-[9px] font-bold uppercase tracking-wide text-emerald-700">
                            <UserRound className="h-2.5 w-2.5" /> Equipo Lumfi
                          </div>
                        )}
                        <p className="whitespace-pre-wrap leading-snug">{m.body}</p>
                        <p className={`text-[9px] mt-1 text-right ${
                          mine ? "text-emerald-50/80" : "text-slate-400"}`}
                        >
                          {new Date(m.created_at).toLocaleTimeString(tx(lang, "en-US", "es-CO", "es-CO"),
                            { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {sending && (
                  <div className="flex justify-start" data-testid="lead-chat-typing">
                    <div className="px-3 py-2 rounded-2xl bg-sky-50 border border-sky-200 text-[11px] text-sky-700 italic">
                      {t.typing}
                    </div>
                  </div>
                )}
                {hasMessages === false && (
                  <p className="text-center text-xs text-slate-400 italic py-6">
                    {t.welcomeIntro}
                  </p>
                )}
              </div>

              {/* Quick actions */}
              <div className="flex items-center justify-between gap-2 px-3 pt-2 pb-1 border-t border-slate-100">
                {!humanRequested ? (
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    onClick={handleRequestHuman}
                    disabled={requestingHuman}
                    data-testid="lead-chat-request-human"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 underline-offset-2 hover:underline disabled:opacity-50"
                  >
                    <UserRound className="h-3 w-3" />
                    {requestingHuman ? t.askingHuman : t.askHuman}
                  </button>
                ) : (
                  <span
                    data-testid="lead-chat-human-pending"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700"
                  >
                    <UserRound className="h-3 w-3" />
                    {t.humanRequested}
                  </span>
                )}
                <span className="text-[10px] text-slate-400">{t.poweredBy}</span>
              </div>

              <form
                className="flex gap-2 p-2 border-t border-slate-200 bg-white"
                onSubmit={handleSend}
              >
                <input /* design-system: ALLOW legacy-todo */
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t.inputPlaceholder}
                  data-testid="lead-chat-input"
                  className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                />
                <button /* design-system: ALLOW legacy-todo */
                  type="submit"
                  disabled={sending || !draft.trim()}
                  data-testid="lead-chat-send"
                  className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                  aria-label={t.sendAria}
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes lead-chat-pulse {
          0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.55); }
          70% { box-shadow: 0 0 0 14px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
        }
        .lead-chat-pulse {
          animation: lead-chat-pulse 2.2s ease-out infinite;
        }
      `}</style>
    </div>
  );
}
